import React from "react";
import { Link } from "react-router-dom";
import { createColumnHelper } from "@tanstack/react-table";
import ProbillPreview from "../common/probill-preview/probillPreview";
import XGSNavLink from "../../ui-components/xgs-nav-link/xgsNavLink";
import { AppointmentNeed, Exception } from "app/data/terminal-insights/models";
import { ManifestTableRow } from "app/data/manifest/manifestProfiles";
import { arrangeName } from "hooks/utils";
import moment from "moment";
import { Routes } from "../../app/route/RoutesConfig";

// Corporate metrics interfaces
interface PerformanceTableRow {
  rank: string;
  terminal_name: string;
  terminal_id: string;
  on_time_percentage: string;
  total_delivered: number;
  late_shipments: number;
}

interface AgingBucketTableRow {
  rank: string;
  terminal_name: string;
  terminal_id: string;
  count_in_2wk_bucket: number;
  total_late: number;
}

interface UnfinalizedPodTableRow {
  rank: string;
  terminal_name: string;
  terminal_id: string;
  total_unfinalized: number;
  over_14_days: number;
  average_age: string;
}

interface CustomerServiceTableRow {
  rank: string;
  terminal_name: string;
  terminal_id: string;
  unsatisfactory_calls: number;
  unsatisfactory_percentage: string;
}

export const getExceptionColumns = () => {
  const exceptionColumnHelper = createColumnHelper<Exception>();
  const exceptionColumns = [
    exceptionColumnHelper.accessor("probill", {
      id: "probill",
      header: "Probill",
      size: 112,
      cell: (cellProps: any) => (
        <ProbillPreview probillNumber={Number(cellProps.getValue())}>
          <span>
            <XGSNavLink
              className="xgs-table-link-cell"
              to={`/shipments/${cellProps.getValue()}`}
              target="__blank"
            >
              {cellProps.getValue()}
            </XGSNavLink>
          </span>
        </ProbillPreview>
      )
    }),
    exceptionColumnHelper.accessor("description", {
      id: "description", 
      header: "Description",
      size: 200
    }),
    exceptionColumnHelper.accessor("addedDate", {
      id: "addedDate",
      header: "Date",
      size: 120
    }),
    exceptionColumnHelper.accessor("reason", {
      id: "reason",
      header: "Reason",
      cell: (cellProps) => (
        <span>
          {cellProps.getValue()}
        </span>
      ),
      size: 120
    })
  ];
  return exceptionColumns;
}

export const getManifestTracking = () => {
const manifestColumnHelper = createColumnHelper<ManifestTableRow>();
  const manifestColumns = [
    manifestColumnHelper.accessor("manifestNumber", {
      id: "manifestNumber",
      header: "Manifest",
      cell: (cellProps) => (<>{cellProps.getValue()}</>),
      size: 120,
    }),
    manifestColumnHelper.accessor("driverName", {
      id: "driverName",
      header: "Driver",
      cell: (cellProps) => {
        return (
          <div className="cell__driver">
            {arrangeName(cellProps.getValue())}
          </div>
        )
      },
      size: 180,
    }),
    manifestColumnHelper.accessor("dispatchedTs", {
      id: "dispatchedTs",
      header: "Dispatched",
      cell: (cellProps) => {
        const date = moment(cellProps.getValue(), "MMM D YYYY hh:mm");
        return (
          <div className="cell__driver">
            {date.format("MMM D")}
            <div className="text-light">{date.format("h:mm A")}</div>
          </div>
        )
      },
      size: 120,
    }),
    manifestColumnHelper.accessor("probills", {
      id: "probills",
      header: "Deliveries",
      cell: (cellProps) => (<>{cellProps.getValue()}</>),
      size: 100,
    }),
    manifestColumnHelper.accessor("exceptions", {
      id: "exceptions",
      header: "Exceptions",
      cell: (cellProps) => (<span className={"cell__exc" + (cellProps.getValue() > 0 ? " --alert" : "")}>{cellProps.getValue()}</span>),
      size: 120,
    }),
  ];
  return manifestColumns;
}

export const getAppointmentColumns = () => {
  const appointmentColumnHelper = createColumnHelper<AppointmentNeed>();
  const appointmentColumns = [
    appointmentColumnHelper.accessor("probill", {
      id: "probill",
      header: "Probill",
      cell: (cellProps) => (
        <ProbillPreview probillNumber={Number(cellProps.getValue())}>
          <span>
            <XGSNavLink
              className="xgs-table-link-cell"
              to={`/shipments/${cellProps.getValue()}`}
              target="__blank"
            >
              {cellProps.getValue()}
            </XGSNavLink>
          </span>
        </ProbillPreview>
      ),
      size: 120
    }),
    appointmentColumnHelper.accessor("eta_date", {
      id: "eta_date",
      header: "ETA Date",
      size: 120
    }),
    appointmentColumnHelper.accessor("consignee", {
      id: "consignee",
      header: "Consignee", 
      size: 280
    }),
    appointmentColumnHelper.accessor("status", {
      id: "status",
      header: "Status",
      cell: (cellProps) => (
        <span>
          {cellProps.getValue()}
        </span>
      ),
      size: 150
    })
  ];
  return appointmentColumns;
}

// Corporate Performance Columns
export const getPerformanceColumns = (selectedDate?: string) => {
  const performanceColumnHelper = createColumnHelper<PerformanceTableRow>();
  const performanceColumns = [
    performanceColumnHelper.accessor("rank", {
      id: "rank",
      header: "Rank",
      size: 80
    }),
    performanceColumnHelper.accessor("terminal_name", {
      id: "terminal_name",
      header: "Terminal",
      size: 200,
      cell: (cellProps) => {
        const terminalId = cellProps.row.original.terminal_id;
        if (terminalId) {
          const dateParam = selectedDate ? `?date=${encodeURIComponent(selectedDate)}` : '';
          return (
            <Link
              to={`/${terminalId}${Routes.terminalInsights}${dateParam}`}
              className="xgs-table-link-cell"
              onClick={(e) => e.stopPropagation()}
            >
              {cellProps.getValue()}
            </Link>
          );
        }
        return <span>{cellProps.getValue()}</span>;
      }
    }),
    performanceColumnHelper.accessor("on_time_percentage", {
      id: "on_time_percentage",
      header: "On-Time %",
      size: 120
    }),
    performanceColumnHelper.accessor("total_delivered", {
      id: "total_delivered",
      header: "Total Delivered",
      size: 140
    }),
    performanceColumnHelper.accessor("late_shipments", {
      id: "late_shipments",
      header: "Late Shipments",
      size: 140
    })
  ];
  return performanceColumns;
}

// Corporate Aging Buckets Columns
export const getAgingBucketColumns = (selectedDate?: string) => {
  const agingColumnHelper = createColumnHelper<AgingBucketTableRow>();
  const agingColumns = [
    agingColumnHelper.accessor("rank", {
      id: "rank",
      header: "Rank",
      size: 80
    }),
    agingColumnHelper.accessor("terminal_name", {
      id: "terminal_name",
      header: "Terminal",
      size: 200,
      cell: (cellProps) => {
        const terminalId = cellProps.row.original.terminal_id;
        if (terminalId) {
          const dateParam = selectedDate ? `?date=${encodeURIComponent(selectedDate)}` : '';
          return (
            <Link
              to={`/${terminalId}${Routes.terminalInsights}${dateParam}`}
              className="xgs-table-link-cell"
              onClick={(e) => e.stopPropagation()}
            >
              {cellProps.getValue()}
            </Link>
          );
        }
        return <span>{cellProps.getValue()}</span>;
      }
    }),
    agingColumnHelper.accessor("count_in_2wk_bucket", {
      id: "count_in_2wk_bucket",
      header: "In 2-Week Bucket",
      size: 160
    }),
    agingColumnHelper.accessor("total_late", {
      id: "total_late",
      header: "Total Late",
      size: 120
    })
  ];
  return agingColumns;
}

// Corporate Unfinalized PODs Columns
export const getUnfinalizedPodColumns = (selectedDate?: string) => {
  const podColumnHelper = createColumnHelper<UnfinalizedPodTableRow>();
  const podColumns = [
    podColumnHelper.accessor("rank", {
      id: "rank",
      header: "Rank",
      size: 80
    }),
    podColumnHelper.accessor("terminal_name", {
      id: "terminal_name",
      header: "Terminal",
      size: 200,
      cell: (cellProps) => {
        const terminalId = cellProps.row.original.terminal_id;
        if (terminalId) {
          const dateParam = selectedDate ? `?date=${encodeURIComponent(selectedDate)}` : '';
          return (
            <Link
              to={`/${terminalId}${Routes.terminalInsights}${dateParam}`}
              className="xgs-table-link-cell"
              onClick={(e) => e.stopPropagation()}
            >
              {cellProps.getValue()}
            </Link>
          );
        }
        return <span>{cellProps.getValue()}</span>;
      }
    }),
    podColumnHelper.accessor("total_unfinalized", {
      id: "total_unfinalized",
      header: "Total",
      size: 100
    }),
    podColumnHelper.accessor("over_14_days", {
      id: "over_14_days",
      header: "Over 14 Days",
      size: 120
    }),
    podColumnHelper.accessor("average_age", {
      id: "average_age",
      header: "Avg Age",
      size: 100
    })
  ];
  return podColumns;
}

// Corporate Customer Service Columns
export const getCustomerServiceColumns = (selectedDate?: string) => {
  const csColumnHelper = createColumnHelper<CustomerServiceTableRow>();
  const csColumns = [
    csColumnHelper.accessor("rank", {
      id: "rank",
      header: "Rank",
      size: 80
    }),
    csColumnHelper.accessor("terminal_name", {
      id: "terminal_name",
      header: "Terminal",
      size: 200,
      cell: (cellProps) => {
        const terminalId = cellProps.row.original.terminal_id;
        if (terminalId) {
          const dateParam = selectedDate ? `?date=${encodeURIComponent(selectedDate)}` : '';
          return (
            <Link
              to={`/${terminalId}${Routes.terminalInsights}${dateParam}`}
              className="xgs-table-link-cell"
              onClick={(e) => e.stopPropagation()}
            >
              {cellProps.getValue()}
            </Link>
          );
        }
        return <span>{cellProps.getValue()}</span>;
      }
    }),
    csColumnHelper.accessor("unsatisfactory_calls", {
      id: "unsatisfactory_calls",
      header: "Unsatisfactory Calls",
      size: 180
    })
  ];
  return csColumns;
}
